import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'components/styleguide/button/Button';

const styles = require('./Dialog.module.scss');

export interface DialogProps {
  cancelText?: string;
  confirmText?: string;
  cancelUrl?: string;
  confirmUrl?: string;
  context?: 'company' | 'talent' | 'neutral';
  isOpen?: boolean;
  message: React.ReactNode;
  onClose?: () => void;
  onConfirm?: () => void;
  title: string;
  triggerId?: string;
}

export default function Dialog({
  cancelText = 'Cancel',
  confirmText = 'Continue',
  cancelUrl,
  confirmUrl,
  context = 'neutral',
  isOpen: isOpenProp = false,
  message,
  onClose,
  onConfirm,
  title,
  triggerId,
}: DialogProps) {
  const [isOpen, setIsOpen] = useState(isOpenProp);
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    onConfirm?.();
  }, [onConfirm]);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  useEffect(() => {
    if (!triggerId) return;

    const trigger = document.getElementById(triggerId);
    if (!trigger) return;

    const handleOpen = () => setIsOpen(true);
    trigger.addEventListener('click', handleOpen);
    return () => trigger.removeEventListener('click', handleOpen);
  }, [triggerId]);

  useEffect(() => {
    if (!isOpen) return;

    const previousFocus = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const titleId = 'dialog-title';
  const messageId = 'dialog-message';

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className={styles.header}>
          <h3 id={titleId} className={styles.title}>{title}</h3>
          <Button
            type="terceary"
            size="S"
            context="neutral"
            iconRight="x"
            onClick={handleClose}
            aria-label="Close dialog"
          />
        </div>

        <div className={styles.body}>
          {typeof message === 'string'
            ? <p id={messageId} className={styles.message}>{message}</p>
            : <div id={messageId} className={styles.message}>{message}</div>
          }
        </div>

        <div className={styles.footer}>
          <Button
            type="secondary"
            size="S"
            context={context}
            onClick={cancelUrl ? undefined : handleClose}
            href={cancelUrl}
          >
            {cancelText}
          </Button>
          <Button
            type="primary"
            size="S"
            context={context}
            onClick={confirmUrl ? undefined : handleConfirm}
            href={confirmUrl}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}
